/**
 * NumericKeypad — POS-grade number entry.
 *
 * Used by: cash tendered on the POS payment screen, quantity entry, the cash
 * drawer count, discount amount entry, and the Staff PIN prompt.
 *
 * Why a custom keypad and not `<input type="number">`: on a fixed POS terminal
 * there is no soft keyboard, and on a tablet the OS keyboard covers half the
 * screen and its layout differs per device. Cashiers work by muscle memory, so
 * the digit positions must be identical on every device — and the targets must
 * be big enough to hit without looking (56px via `--sa-touch-lg`).
 *
 * Value handling: the value is a STRING of digits with an optional decimal
 * separator, and the parent converts to minor units. That avoids float drift
 * while typing, which is exactly how "25.000" becomes 24999 in other systems.
 * With `exponent = 0` (VND) the decimal key is hidden entirely.
 *
 * Accessibility: real buttons with `aria-label`, physical keyboard input works
 * too (digits, Backspace, Escape, Enter), and the display is `aria-live` so a
 * screen reader tracks the amount.
 */
import { useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../utils.js';

export interface NumericKeypadProps {
  /** Digit string, e.g. "125000" or "12.5". Controlled. */
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** Currency exponent; 0 (VND) hides the decimal key. */
  readonly exponent?: number;
  /** Confirm action, wired to Enter as well. */
  readonly onSubmit?: () => void;
  readonly submitLabel?: string;
  /** Quick amounts rendered above the grid (50k, 100k, 200k, 500k…). */
  readonly quickValues?: readonly { readonly label: string; readonly value: string }[];
  /** Rendered above the grid — usually a formatted MoneyText of `value`. */
  readonly display?: ReactNode;
  readonly disabled?: boolean;
  /** Refuse input longer than this many characters. */
  readonly maxLength?: number;
  readonly className?: string;
}

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

export function NumericKeypad({
  value,
  onChange,
  exponent = 0,
  onSubmit,
  submitLabel = 'Xác nhận',
  quickValues,
  display,
  disabled = false,
  maxLength = 12,
  className,
}: NumericKeypadProps): React.JSX.Element {
  const append = useCallback(
    (char: string) => {
      if (disabled) return;
      if (char === '.' && (exponent === 0 || value.includes('.'))) return;
      if (value.length >= maxLength) return;
      // Stop "007": a leading zero only survives before a decimal point.
      if (value === '0' && char !== '.') {
        onChange(char);
        return;
      }
      // Respect the currency exponent: two decimals for USD, none for VND.
      if (value.includes('.')) {
        const decimals = value.split('.')[1] ?? '';
        if (char !== '.' && decimals.length >= exponent) return;
      }
      onChange(value + char);
    },
    [disabled, exponent, maxLength, onChange, value],
  );

  const backspace = useCallback(() => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  }, [disabled, onChange, value]);

  const clear = useCallback(() => {
    if (disabled) return;
    onChange('');
  }, [disabled, onChange]);

  // A physical keyboard must work too: many POS terminals have one, and it is
  // faster than tapping for a trained cashier.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (disabled) return;
      if (event.key >= '0' && event.key <= '9') {
        append(event.key);
      } else if (event.key === 'Backspace') {
        backspace();
      } else if (event.key === 'Escape') {
        clear();
      } else if (event.key === 'Enter' && onSubmit !== undefined) {
        onSubmit();
      } else if ((event.key === '.' || event.key === ',') && exponent > 0) {
        append('.');
      } else {
        return;
      }
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [append, backspace, clear, disabled, exponent, onSubmit]);

  const keyClass = cn(
    'flex min-h-touch-lg items-center justify-center rounded-lg text-2xl font-semibold',
    'bg-surface text-fg ring-1 ring-inset ring-line-strong',
    'transition-colors active:bg-surface-sunken hover:bg-surface-alt',
    'outline-none focus-visible:ring-2 focus-visible:ring-ring',
    'disabled:opacity-40 disabled:cursor-not-allowed',
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {display !== undefined ? (
        <div
          aria-live="polite"
          className="flex min-h-touch-lg items-center justify-end rounded-lg bg-surface-sunken px-4 text-right text-3xl font-semibold text-fg"
        >
          {display}
        </div>
      ) : null}

      {quickValues !== undefined && quickValues.length > 0 ? (
        <div className="grid grid-cols-4 gap-2">
          {quickValues.map((q) => (
            <button
              key={q.label}
              type="button"
              disabled={disabled}
              onClick={() => onChange(q.value)}
              className={cn(keyClass, 'min-h-touch text-base')}
            >
              {q.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-3 gap-2">
        {DIGITS.map((d) => (
          <button
            key={d}
            type="button"
            aria-label={`Số ${d}`}
            disabled={disabled}
            onClick={() => append(d)}
            className={keyClass}
          >
            {d}
          </button>
        ))}

        {exponent > 0 ? (
          <button
            type="button"
            aria-label="Dấu thập phân"
            disabled={disabled}
            onClick={() => append('.')}
            className={keyClass}
          >
            ,
          </button>
        ) : (
          <button
            type="button"
            aria-label="Xóa hết"
            disabled={disabled}
            onClick={clear}
            className={cn(keyClass, 'text-base text-danger-fg')}
          >
            C
          </button>
        )}

        <button type="button" aria-label="Số 0" disabled={disabled} onClick={() => append('0')} className={keyClass}>
          0
        </button>

        <button
          type="button"
          aria-label="Xóa một ký tự"
          disabled={disabled}
          onClick={backspace}
          className={cn(keyClass, 'text-xl')}
        >
          ⌫
        </button>
      </div>

      {onSubmit !== undefined ? (
        <button
          type="button"
          disabled={disabled}
          onClick={onSubmit}
          className={cn(
            'flex min-h-touch-lg items-center justify-center rounded-lg bg-brand-600 text-lg font-semibold text-white',
            'transition-colors hover:bg-brand-700 active:bg-brand-800',
            'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {submitLabel}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Convert the keypad's digit string to minor units.
 * `"125"` with exponent 0 -> 125; `"12.34"` with exponent 2 -> 1234.
 */
export function keypadToMinor(value: string, exponent = 0): number {
  if (value === '' || value === '.') return 0;
  const [whole = '0', fraction = ''] = value.split('.');
  const padded = (fraction + '0'.repeat(exponent)).slice(0, exponent);
  const combined = `${whole}${padded}`;
  const parsed = Number.parseInt(combined, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}
